import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FiChevronLeft, FiChevronRight, FiCalendar,
  FiClock, FiMapPin, FiEdit2, FiCheckCircle, FiArrowLeft, FiUser,
  FiHome, FiXCircle
} from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { visitaAniversarioService } from '../../services/api';
import './VisitasAniversario.css';

const MESES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

const DIAS_SEMANA = ['Domingo','Segunda','Terça','Quarta','Quinta','Sexta','Sábado'];

const STATUS_OPCOES = [
  { value: 'ativas',                 label: 'Ativas'       },
  { value: 'aguardando_confirmacao', label: 'Aguardando'   },
  { value: 'confirmada',             label: 'Confirmadas'  },
  { value: 'ausente',                label: 'Ausentes'     },
  { value: 'realizada',              label: 'Realizadas'   },
  { value: 'cancelada',              label: 'Canceladas'   },
];

const primeiroNome = (nome) => (nome || '').split(' ')[0];

const STATUS_INFO = {
  aguardando_confirmacao: { label: 'Aguardando', cor: '#f59e0b' },
  confirmada:             { label: 'Confirmada', cor: '#10b981' },
  ausente:                { label: 'Ausente',    cor: '#6366f1' },
  realizada:              { label: 'Realizada',  cor: '#3b82f6' },
  cancelada:              { label: 'Cancelada',  cor: '#ef4444' },
};

const formatarDataBR = (data) => {
  const d = new Date(data);
  return `${String(d.getUTCDate()).padStart(2,'0')}/${String(d.getUTCMonth()+1).padStart(2,'0')}/${d.getUTCFullYear()}`;
};

const dataISO = (data) => new Date(data).toISOString().slice(0, 10);

const VisitasAgendadas = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hoje = new Date();

  const qp = new URLSearchParams(location.search);
  const mesPadrao = parseInt(qp.get('mes'), 10) || (hoje.getMonth() + 1);
  const anoPadrao = parseInt(qp.get('ano'), 10) || hoje.getFullYear();

  const [mes, setMes] = useState(mesPadrao);
  const [ano, setAno] = useState(anoPadrao);
  const [filtro, setFiltro] = useState('ativas');
  const [carregando, setCarregando] = useState(true);
  const [visitas, setVisitas] = useState([]);
  const [modalEditar, setModalEditar] = useState(null);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    carregar();
  }, [mes, ano]); // eslint-disable-line

  const carregar = async () => {
    try {
      setCarregando(true);
      // Carrega todas (sem filtro de status) e filtra no cliente
      const resp = await visitaAniversarioService.listar({ mes, ano });
      setVisitas(resp.data || []);
    } catch (err) {
      console.error(err);
      toast.error('Erro ao carregar visitas');
    } finally {
      setCarregando(false);
    }
  };

  const mudarMes = (delta) => {
    let m = mes + delta;
    let a = ano;
    if (m > 12) { m = 1; a++; }
    if (m < 1)  { m = 12; a--; }
    setMes(m);
    setAno(a);
  };

  const visitasFiltradas = useMemo(() => {
    return visitas.filter(v => {
      if (filtro === 'ativas') return v.status === 'aguardando_confirmacao' || v.status === 'confirmada';
      return v.status === filtro;
    });
  }, [visitas, filtro]);

  const acaoVisita = async (id, novoStatus, confirmarMsg) => {
    if (confirmarMsg && !window.confirm(confirmarMsg)) return;
    try {
      await visitaAniversarioService.atualizar(id, { status: novoStatus });
      const msgs = {
        realizada: 'Visita marcada como realizada!',
        ausente:   'Registrado que não havia ninguém em casa.',
        pendente:  'Visita cancelada. Voltou para pendentes.',
      };
      toast.success(msgs[novoStatus] || 'Atualizado!');
      carregar();
    } catch (err) {
      console.error(err);
      toast.error('Erro ao atualizar visita');
    }
  };

  const grupos = useMemo(() => {
    const m = {};
    visitasFiltradas.forEach(v => {
      const k = dataISO(v.dataVisita);
      if (!m[k]) m[k] = [];
      m[k].push(v);
    });
    return Object.keys(m).sort().map(data => ({
      data,
      diaSemana: DIAS_SEMANA[new Date(data + 'T12:00:00Z').getUTCDay()],
      visitas: m[data].sort((a, b) => (a.horaVisita || '').localeCompare(b.horaVisita || '')),
    }));
  }, [visitasFiltradas]);

  const abrirModalEditar = (v) => {
    setModalEditar({
      visitaId: v.id,
      criancaNome: v.crianca?.nomeCompleto || '',
      dataVisita: new Date(v.dataVisita).toISOString().slice(0, 10),
      horaVisita: v.horaVisita || '15:00',
      endereco: v.endereco || v.crianca?.endereco || '',
      status: v.status,
      observacoes: v.observacoes || '',
    });
  };

  const salvarEdicao = async () => {
    if (!modalEditar.dataVisita || !modalEditar.horaVisita) {
      toast.warn('Preencha data e hora');
      return;
    }
    try {
      setSalvando(true);
      await visitaAniversarioService.atualizar(modalEditar.visitaId, {
        dataVisita: modalEditar.dataVisita,
        horaVisita: modalEditar.horaVisita,
        endereco: modalEditar.endereco,
        status: modalEditar.status,
        observacoes: modalEditar.observacoes,
      });
      toast.success('Visita atualizada!');
      setModalEditar(null);
      carregar();
    } catch (err) {
      console.error(err);
      toast.error('Erro ao salvar');
    } finally {
      setSalvando(false);
    }
  };

  const handleMensagemGrupo = async (data) => {
    const waWin = window.open('about:blank', '_blank');
    try {
      const resp = await visitaAniversarioService.mensagemGrupo(data);
      if (!resp.data.texto) {
        waWin?.close();
        toast.info('Sem visitas para esta data');
        return;
      }
      const url = `https://wa.me/?text=${encodeURIComponent(resp.data.texto)}`;
      if (waWin) {
        waWin.location.href = url;
      } else {
        window.location.href = url;
      }
    } catch (err) {
      waWin?.close();
      console.error(err);
      toast.error('Erro ao gerar mensagem');
    }
  };


  return (
    <div className="va-container">
      <header className="va-header">
        <button
          className="va-btn-secondary"
          onClick={() => navigate('/reuniao-criancas/aniversariantes')}
        >
          <FiArrowLeft /> Voltar
        </button>
        <div className="va-header-info">
          <h1><FiCalendar /> Visitas agendadas</h1>
          <p>Visitas organizadas por data</p>
        </div>
      </header>

      <div className="va-mes-seletor">
        <button onClick={() => mudarMes(-1)} aria-label="Mês anterior"><FiChevronLeft /></button>
        <div className="va-mes-label">
          <strong>{MESES[mes - 1]}</strong>
          <span>{ano}</span>
        </div>
        <button onClick={() => mudarMes(1)} aria-label="Próximo mês"><FiChevronRight /></button>
      </div>

      <div className="va-filtros">
        {STATUS_OPCOES.map(opt => (
          <button
            key={opt.value}
            className={`va-chip ${filtro === opt.value ? 'va-chip-active' : ''}`}
            onClick={() => setFiltro(opt.value)}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {carregando ? (
        <div className="va-loading">Carregando...</div>
      ) : grupos.length === 0 ? (
        <div className="va-empty">
          <FiCalendar className="va-empty-icon" />
          <p>Nenhuma visita encontrada.</p>
        </div>
      ) : (
        <div className="va-grupos">
          {grupos.map(g => (
            <section key={g.data} className="va-grupo">
              <header className="va-grupo-header">
                <div>
                  <h3>{g.diaSemana} — {formatarDataBR(g.data)}</h3>
                  <p>{g.visitas.length} visita(s)</p>
                </div>
                <button
                  className="va-btn va-btn-wa"
                  onClick={() => handleMensagemGrupo(g.data)}
                >
                  <FaWhatsapp /> Avisar grupo
                </button>
              </header>

              <div className="va-grupo-itens">
                {g.visitas.map(v => {
                  const si = STATUS_INFO[v.status] || STATUS_INFO.aguardando_confirmacao;
                  return (
                    <div key={v.id} className="va-visita-item" style={{ borderLeftColor: si.cor }}>
                      <div className="va-visita-hora">
                        <FiClock />
                        <span>{v.horaVisita}</span>
                      </div>
                      <div className="va-visita-info">
                        <h4>{v.crianca?.nomeCompleto}</h4>
                        {v.endereco && (
                          <p><FiMapPin /> {v.endereco}</p>
                        )}
                        <div className="va-visita-meta">
                          <span
                            className="va-status-tag"
                            style={{ backgroundColor: si.cor + '22', color: si.cor }}
                          >
                            {si.label}
                          </span>
                          {v.agendadoPor?.nome && (
                            <span className="va-agendado-por">
                              <FiUser size={11} /> {primeiroNome(v.agendadoPor.nome)}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="va-visita-acoes">
                        <button
                          className="va-btn-icon va-btn-icon--edit"
                          onClick={() => abrirModalEditar(v)}
                          title="Editar visita"
                        >
                          <FiEdit2 />
                        </button>
                        {(v.status === 'aguardando_confirmacao' || v.status === 'confirmada') && (<>
                          <button
                            className="va-btn-icon va-btn-icon--ok"
                            onClick={() => acaoVisita(v.id, 'realizada')}
                            title="Marcar como realizada"
                          >
                            <FiCheckCircle />
                          </button>
                          <button
                            className="va-btn-icon va-btn-icon--ausente"
                            onClick={() => acaoVisita(v.id, 'ausente', 'Registrar que não havia ninguém em casa?')}
                            title="Ninguém em casa"
                          >
                            <FiHome />
                          </button>
                          <button
                            className="va-btn-icon va-btn-icon--cancel"
                            onClick={() => acaoVisita(v.id, 'pendente', 'Cancelar esta visita? Ela voltará para pendentes.')}
                            title="Cancelar visita"
                          >
                            <FiXCircle />
                          </button>
                        </>)}
                        {v.status === 'ausente' && (
                          <button
                            className="va-btn-icon va-btn-icon--cancel"
                            onClick={() => acaoVisita(v.id, 'pendente', 'Cancelar esta visita? Ela voltará para pendentes.')}
                            title="Cancelar visita"
                          >
                            <FiXCircle />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}

      {modalEditar && (
        <div className="va-modal-overlay" onClick={() => setModalEditar(null)}>
          <div className="va-modal" onClick={(e) => e.stopPropagation()}>
            <header className="va-modal-header">
              <h2>Editar visita</h2>
              <p>{modalEditar.criancaNome}</p>
            </header>

            <div className="va-modal-body">
              <div className="va-form-row">
                <div className="va-form-group">
                  <label>Data da visita</label>
                  <input
                    type="date"
                    value={modalEditar.dataVisita}
                    onChange={(e) => setModalEditar({ ...modalEditar, dataVisita: e.target.value })}
                  />
                </div>
                <div className="va-form-group">
                  <label>Horário</label>
                  <input
                    type="time"
                    value={modalEditar.horaVisita}
                    onChange={(e) => setModalEditar({ ...modalEditar, horaVisita: e.target.value })}
                  />
                </div>
              </div>

              <div className="va-form-group">
                <label>Endereço</label>
                <input
                  type="text"
                  value={modalEditar.endereco}
                  onChange={(e) => setModalEditar({ ...modalEditar, endereco: e.target.value })}
                  placeholder="Endereço da visita"
                />
              </div>

              <div className="va-form-group">
                <label>Status</label>
                <select
                  value={modalEditar.status}
                  onChange={(e) => setModalEditar({ ...modalEditar, status: e.target.value })}
                >
                  <option value="aguardando_confirmacao">Aguardando confirmação</option>
                  <option value="confirmada">Confirmada</option>
                  <option value="realizada">Realizada</option>
                  <option value="ausente">Ausente</option>
                  <option value="cancelada">Cancelada</option>
                </select>
              </div>

              <div className="va-form-group">
                <label>Observações</label>
                <textarea
                  rows="2"
                  value={modalEditar.observacoes}
                  onChange={(e) => setModalEditar({ ...modalEditar, observacoes: e.target.value })}
                  placeholder="Observações (opcional)"
                />
              </div>
            </div>

            <footer className="va-modal-footer">
              <button className="va-btn va-btn-ghost" onClick={() => setModalEditar(null)}>
                Cancelar
              </button>
              <button className="va-btn va-btn-primary" onClick={salvarEdicao} disabled={salvando}>
                {salvando ? 'Salvando...' : 'Salvar'}
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
};

export default VisitasAgendadas;
